import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useDebounce } from "@hydrologiq/shared/hooks/useDebounce"
import { VStack, HStack, Input, Text, Accordion } from "@chakra-ui/react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useLazyQueryWithEndpoint, useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { DateTime, hours, utc, zone } from "timezonecomplete"

import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "@hydrologiq/shared/utils/toast"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import AvailableAssetCombobox, {
  AssetItem,
} from "@hydrologiq/shared/components/activities/inputs/AvailableAssetCombobox"
import { useMultiAssetValidation } from "@hydrologiq/shared/hooks/useMultiAssetValidation"
import { OrderWorkflowProvider, useOrderWorkflow } from "@hydrologiq/shared/contexts/OrderWorkflowContext"
import AssetAvailabilityCalendarDialogSimple from "@hydrologiq/shared/components/calendar/AssetAvailabilityCalendarDialog"
import { CalendarButton } from "@hydrologiq/shared/content/components/Button"
import SelectedAssetsList from "@hydrologiq/shared/components/SelectedAssetsList"
import UnavailableAssetsWarning from "@hydrologiq/shared/components/UnavailableAssetsWarning"

interface ActivityValidationProps {
  dates?: boolean
  assets?: boolean
}

interface ActivityFeatureProps {
  multipleAssets?: boolean
  quantityEditable?: boolean
}

interface ActivitiesDetailsEditorProps {
  activity: gqlTypes.Activity
  onSuccess: () => void | Promise<void>
  onCancel: () => void
  validation?: ActivityValidationProps
  features?: ActivityFeatureProps
}

// Format existing dates for input fields (convert from ISO to yyyy-MM-dd)
const getDefaultDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) return ""
  try {
    const tz = zone(IANATimeZone())

    // Handle dates that come without timezone info (treat as UTC)
    let dt: DateTime
    if (!isoDate.includes("Z") && !isoDate.includes("+") && !isoDate.includes("-", 10)) {
      dt = new DateTime(isoDate, utc()).toZone(tz)
    } else {
      dt = new DateTime(isoDate).toZone(tz)
    }

    return dt.format("yyyy-MM-dd'T'HH:mm")
  } catch (e) {
    console.error("Error parsing date:", e, isoDate)
    return ""
  }
}

const ActivitiesDetailsEditorInner: React.FC<ActivitiesDetailsEditorProps> = ({
  activity,
  onSuccess,
  onCancel,
  validation = { dates: false, assets: true },
  features = { multipleAssets: false, quantityEditable: false },
}) => {
  const [error, setError] = useState<string | null>(null)

  // Get context for calendar
  const { state, openCalendar, updateOrderDates } = useOrderWorkflow()

  // Get default end time as 1 hour after start time
  const getDefaultEndDateTime = (startDateTime: string, endDateTime?: string) => {
    try {
      const tz = zone(IANATimeZone())
      const start = new DateTime(startDateTime, "YYYY-MM-DD'T'HH:mm", tz)
      const end = endDateTime ? new DateTime(endDateTime, "YYYY-MM-DD'T'HH:mm", tz) : start.add(hours(1)) // Add 1 hour to start time
      return end.format("yyyy-MM-dd'T'HH:mm")
    } catch (e) {
      console.error("Error calculating default end date:", e)
      // Fallback: just return the start time (shouldn't happen in normal flow)
      return startDateTime
    }
  }

  // Use controlled state for inputs
  const [startDate, setStartDate] = useState<string>(() => getDefaultDate(activity.start))
  const [endDate, setEndDate] = useState<string>(() =>
    getDefaultEndDateTime(getDefaultDate(activity.start), activity.end ?? undefined)
  )

  const [selectedAssets, setSelectedAssets] = useState<AssetItem[]>(
    activity.assets?.map((a) => ({ id: a.id, name: a.name })) || []
  )
  const [dateValidationError, setDateValidationError] = useState<string | null>(null)
  const [quantity, setQuantity] = useState<string>(activity.quantity?.toString() || "")
  const [availabilityWarning, setAvailabilityWarning] = useState<string | null>(null)
  const [lastChangedField, setLastChangedField] = useState<"start" | "end" | null>(null)

  // Debounce the date values to prevent excessive validation
  const debouncedStartDate = useDebounce(startDate, 250)
  const debouncedEndDate = useDebounce(endDate, 250)

  // Sync context with local state when calendar closes
  useEffect(() => {
    if (!state.calendarOpen && state.selectedAssets && state.start && state.end) {
      // Calendar was closed and assets may have been updated
      setSelectedAssets(state.selectedAssets)
      setStartDate(state.start)
      setEndDate(state.end)
    }
  }, [state.calendarOpen, state.selectedAssets, state.start, state.end])

  // Update state when fulfillment changes (e.g., when dialog is reopened with different fulfillment)
  useEffect(() => {
    setStartDate(getDefaultDate(activity.start))
    setEndDate(getDefaultDate(activity.end))
    setSelectedAssets(activity.assets?.map((a) => ({ id: a.id, name: a.name })) || [])
    setQuantity(activity.quantity?.toString() || "")
    setDateValidationError(null)
  }, [activity.id, activity.start, activity.end, activity.assets, activity.quantity])

  // Use multi-asset validation for all selected assets
  // Note: Currently only supports orderId exclusion, activity exclusion can be added later
  const { unavailableAssets } = useMultiAssetValidation({
    selectedAssets,
    startDate: debouncedStartDate,
    endDate: debouncedEndDate,
    activitiesExclude: activity.id,
  })
  // GraphQL query for checking activity availability
  const [checkAvailabilityQuery, { loading: checkAvailabilityLoading }] = useLazyQueryWithEndpoint(
    gqlTypes.CheckActivityAvailabilityDocument,
    {
      fetchPolicy: "network-only", // Always check fresh data
      onCompleted: (data) => {
        if (data?.checkActivityAvailability) {
          const result = data.checkActivityAvailability
          if (!result.available && result.conflicts && result.conflicts.length > 0) {
            try {
              const conflict = result.conflicts[0]
              const tz = zone(IANATimeZone())
              const endWithTz = (date: string) => (date.includes("Z") ? date : date + "Z")
              const conflictDate = new DateTime(endWithTz(conflict.start), utc()).toZone(tz).format("MMM dd, YYYY")
              // const conflictStart = new DateTime(endWithTz(conflict.start), utc()).toZone(tz).format("HH:mm")
              // const conflictEnd = new DateTime(endWithTz(conflict.end), utc()).toZone(tz).format("HH:mm")

              // Build warning message
              const assetName = conflict.asset_name || conflict.asset_id || "This asset"
              const activityKind = conflict.activity_kind || "activity"
              const activityName = conflict.activity_name ? ` (${conflict.activity_name})` : ""
              const warningMsg = `There is already a ${activityKind} activity${activityName} during the specified time period on ${conflictDate}.`

              console.log("Setting availability warning:", warningMsg)
              setAvailabilityWarning(warningMsg)
            } catch (error) {
              console.error("Error parsing conflict date:", error)
            }
          } else {
            console.log("No conflicts found, clearing warning")
            setAvailabilityWarning(null)
          }
        }
      },
      onError: (error) => {
        console.error("Error checking availability:", error)
        // Don't show error to user, just log it
      },
    },
    "primary"
  )
  // Check activity availability
  const checkAvailability = async () => {
    // Only check if we have dates and an asset selected
    if (!startDate || !endDate || !selectedAssets.length) {
      setAvailabilityWarning(null)
      return
    }

    // Don't check if dates are invalid
    if (dateValidationError) {
      return
    }

    try {
      const tz = zone(IANATimeZone())
      const start = new DateTime(startDate, "YYYY-MM-DD'T'HH:mm", tz)
      const end = new DateTime(endDate, "YYYY-MM-DD'T'HH:mm", tz)
      // Call GraphQL query with full_day_check disabled
      await checkAvailabilityQuery({
        variables: {
          assets: selectedAssets.map((a) => a.id).join(","),
          start: start.toZone(utc()).toIsoString(),
          end: end.toZone(utc()).toIsoString(),
          exclude_activity: activity.id,
          full_day_check: false, // Allow multiple activities on same day with different times
        },
      })
    } catch (error) {
      console.error("Error in checkAvailability:", error)
      // Error already handled in onError callback
    }
  }
  // Validate dates whenever they change
  useEffect(() => {
    if (startDate && endDate) {
      try {
        const tz = zone(IANATimeZone())
        const start = new DateTime(startDate, "YYYY-MM-DD'T'HH:mm", tz)
        const end = new DateTime(endDate, "YYYY-MM-DD'T'HH:mm", tz)

        if (end.valueOf() <= start.valueOf()) {
          // Only show validation error if end was the last changed field
          if (lastChangedField === "end") {
            setDateValidationError("End date/time must be after start date/time")
          }
        } else {
          setDateValidationError(null)
        }
      } catch {
        // Invalid date format, clear validation error
        setDateValidationError(null)
      }
    } else {
      setDateValidationError(null)
    }
  }, [startDate, endDate, lastChangedField])

  // Check availability when dates or asset changes
  useEffect(() => {
    if (validation.dates) {
      const timeoutId = setTimeout(() => {
        checkAvailability()
      }, 500) // Debounce by 500ms

      return () => clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedAssets, dateValidationError, validation])

  // Memoize asset ID arrays to prevent re-creating on every render
  const unavailableAssetIds = useMemo(() => unavailableAssets.map((a) => a.id), [unavailableAssets])
  const selectedAssetIds = useMemo(() => selectedAssets.map((a) => a.id), [selectedAssets])

  // Handler for adding asset
  const handleAddAsset = useCallback(
    (asset: AssetItem | undefined) => {
      if (!asset) {
        return
      }

      // Check if asset is already selected
      if (selectedAssets.some((a) => a.id === asset.id)) {
        showErrorToast("Asset Already Selected", "This asset is already added to the activity")
        return
      }

      // Add asset immediately - validation happens via useMultiAssetValidation
      setSelectedAssets((prev) => [...prev, asset])
    },
    [selectedAssets]
  )

  // Handler for removing asset
  const handleRemoveAsset = useCallback((assetId: string) => {
    setSelectedAssets((prev) => prev.filter((a) => a.id !== assetId))
  }, [])

  // Handler for removing unavailable assets in bulk
  const handleRemoveUnavailableAssets = useCallback((assetIds: string[]) => {
    setSelectedAssets((prev) => prev.filter((a) => !assetIds.includes(a.id)))
  }, [])

  // Validate dates whenever they change (using debounced values)
  useEffect(() => {
    if (debouncedStartDate && debouncedEndDate) {
      try {
        const tz = zone(IANATimeZone())
        const start = new DateTime(debouncedStartDate, "YYYY-MM-DD'T'HH:mm", tz)
        const end = new DateTime(debouncedEndDate, "YYYY-MM-DD'T'HH:mm", tz)

        if (end.valueOf() < start.valueOf()) {
          setDateValidationError("End date/time cannot be before start date/time")
        } else {
          setDateValidationError(null)
        }
      } catch {
        // Invalid date format, clear validation error
        setDateValidationError(null)
      }
    } else {
      setDateValidationError(null)
    }
  }, [debouncedStartDate, debouncedEndDate])

  const [updateActivity, { loading }] = useMutationWithEndpoint(
    gqlTypes.UpdateActivityDocument,
    {
      refetchQueries: ["GetSuggestedFulfilments", "AssetAvailabilityCalendar"],
      awaitRefetchQueries: false,
    },
    "primary"
  )

  const handleSubmit = async (): Promise<boolean> => {
    let toastId: string | undefined
    try {
      setError(null)

      // Check for validation errors before submitting
      if (dateValidationError) {
        setError(dateValidationError)
        showErrorToast("Validation Error", dateValidationError)
        return false
      }

      if (unavailableAssets.length > 0) {
        const errorMessage = `${unavailableAssets.length} asset(s) are not available for the selected dates. Please remove them or change the dates.`
        setError(errorMessage)
        showErrorToast("Assets Unavailable", errorMessage)
        return false
      }

      if (selectedAssets.length === 0) {
        const errorMessage = "Please select at least one asset"
        setError(errorMessage)
        showErrorToast("Validation Error", errorMessage)
        return false
      }

      toastId = showLoadingToast("Updating fulfillment details...")

      const tz = zone(IANATimeZone())
      const vars: gqlTypes.UpdateActivityMutationVariables = {
        activity: activity.id,
      }

      // Parse, validate, and format dates if provided
      if (startDate && endDate) {
        try {
          const start = new DateTime(startDate, "YYYY-MM-DD'T'HH:mm", tz)
          const end = new DateTime(endDate, "YYYY-MM-DD'T'HH:mm", tz)

          // Validate end is not before start
          if (end.valueOf() < start.valueOf()) {
            if (toastId) dismissToast(toastId)
            const errorMessage = "End date/time cannot be before start date/time"
            setError(errorMessage)
            showErrorToast("Validation Error", errorMessage)
            return false
          }

          vars.start = start.toZone(utc()).toIsoString()
          vars.end = end.toZone(utc()).toIsoString()
        } catch (error) {
          if (toastId) dismissToast(toastId)
          const errorMessage = "Invalid date format. Please check your dates."
          setError(errorMessage)
          showErrorToast("Validation Error", errorMessage)
          return false
        }
      }

      // Add all selected assets
      if (selectedAssets.length > 0) {
        vars.assets = selectedAssets.map((asset) => ({ asset: asset.id }))
      }

      const result = await updateActivity({
        variables: vars,
      })

      // Check for GraphQL errors first
      if (result.errors && result.errors.length > 0) {
        const errorMessage = result.errors[0].message || "Failed to update activity details. Please try again."
        if (toastId) dismissToast(toastId)
        setError(errorMessage)
        showErrorToast("Update Failed", errorMessage)
        return false
      }

      if (result.data?.updateActivity) {
        if (toastId) dismissToast(toastId)
        showSuccessToast("Activity details updated successfully")
        await onSuccess()
        return true
      } else {
        // Log the actual response for debugging
        console.error("UpdateActivity response:", result)
        if (toastId) dismissToast(toastId)
        const errorMessage = "Failed to update activity details. The server returned an unexpected response."
        setError(errorMessage)
        showErrorToast("Update Failed", errorMessage)
        return false
      }
    } catch (error: any) {
      console.error("Error updating activity details:", error)
      if (toastId) {
        dismissToast(toastId)
      }
      // Show user-friendly error message
      const errorMessage = error?.message || "Unable to update activity details. Please try again."
      setError(errorMessage)
      showErrorToast("Update Failed", "Unable to update activity details. Please try again.")
      return false
    }
  }

  return (
    <Form
      actionType="confirm"
      onSubmit={handleSubmit}
      loading={loading || checkAvailabilityLoading}
      canSubmit={!availabilityWarning && !error && !dateValidationError}
    >
      <VStack gap={4} align="stretch">
        {(error || dateValidationError) && <ErrorAlert title="Error" message={error || dateValidationError || ""} />}
        {availabilityWarning && !error && !dateValidationError && (
          <ErrorAlert title="Scheduling Conflict" message={availabilityWarning} />
        )}

        <Text fontSize="sm" color="fg.muted">
          Update the asset, dates, and quantity for this fulfillment.
        </Text>

        <HStack gap={2} width="100%" justifyContent="space-between">
          <HStack gap={2} width="100%">
            <Field label="Start Date" required>
              <Input
                name="start"
                type="datetime-local"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value)
                  setLastChangedField("start")
                }}
              />
            </Field>
            <Field label="End Date" required>
              <Input
                name="end"
                type="datetime-local"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value)
                  setLastChangedField("end")
                }}
              />
            </Field>
          </HStack>
        </HStack>

        {features.quantityEditable && (
          <Field label="Quantity (kg)" required={false}>
            <Input
              name="quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Enter quantity"
              step="0.01"
            />
          </Field>
        )}

        <Accordion.Root collapsible defaultValue={["assets"]} variant="primary">
          <Accordion.Item key="assets" value="assets">
            <Accordion.ItemTrigger>
              <HStack justify="space-between" width="100%">
                <HStack gap={2}>
                  <Text>
                    Asset{features.multipleAssets ? "s" : ""}{" "}
                    {features.multipleAssets ? selectedAssets.length > 0 && `(${selectedAssets.length})` : ""}
                  </Text>
                  {selectedAssets.length === 0 && (
                    <Text fontSize="xs" color="red.500">
                      Required
                    </Text>
                  )}
                </HStack>
                {startDate && endDate && (
                  <CalendarButton
                    aria-label="View availability calendar"
                    onClick={(e) => {
                      e.stopPropagation()
                      updateOrderDates(startDate, endDate)
                      openCalendar(startDate, endDate, selectedAssets, undefined, features.multipleAssets)
                    }}
                    disabled={!startDate || !endDate || !!dateValidationError}
                  />
                )}
              </HStack>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent>
              <VStack gap={3} align="stretch" p={2}>
                {/* Show warning for unavailable assets */}
                {unavailableAssets.length > 0 && (
                  <UnavailableAssetsWarning
                    assets={unavailableAssets}
                    onRemove={handleRemoveUnavailableAssets}
                    onViewCalendar={() => {
                      updateOrderDates(startDate, endDate)
                      openCalendar(startDate, endDate, selectedAssets)
                    }}
                  />
                )}

                {/* Show selected assets list */}
                <SelectedAssetsList
                  assets={selectedAssets}
                  unavailableAssetIds={unavailableAssetIds}
                  onRemove={handleRemoveAsset}
                  singleAsset={!features.multipleAssets}
                />

                {/* Add asset section */}
                {features.multipleAssets ||
                  (selectedAssets.length === 0 && (
                    <VStack gap={2} align="stretch">
                      <Text fontSize="sm" fontWeight="semibold">
                        Add new asset
                      </Text>
                      <AvailableAssetCombobox
                        startDate={startDate}
                        endDate={endDate}
                        onItemSelection={handleAddAsset}
                        value={null}
                        disabled={!!dateValidationError}
                        excludedAssetIds={selectedAssetIds}
                        clearAfterSelection={true}
                      />
                    </VStack>
                  ))}
              </VStack>
            </Accordion.ItemContent>
          </Accordion.Item>
        </Accordion.Root>
      </VStack>

      {/* Calendar Dialog */}
      <AssetAvailabilityCalendarDialogSimple />
    </Form>
  )
}

// Wrap with context provider
const ActivitiesDetailsEditor: React.FC<ActivitiesDetailsEditorProps> = (props) => {
  return (
    <OrderWorkflowProvider>
      <ActivitiesDetailsEditorInner {...props} />
    </OrderWorkflowProvider>
  )
}

export default ActivitiesDetailsEditor
